
import { useLocale, useTranslations } from '@/i18n'
import { locales, localeNames, localeCodes, FlagIcon } from '@/i18n/config'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Check, ChevronRight } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface LanguageSwitcherProps {
  variant?: 'default' | 'minimal'
}

// SVG flag with aspect-ratio for perfect scaling at any size
function Flag({ locale, size = 'md' }: { locale: typeof locales[number]; size?: 'sm' | 'md' }) {
  const heights = {
    sm: 'h-3',
    md: 'h-4',
  }

  return (
    <FlagIcon
      locale={locale}
      className={`${heights[size]} aspect-[3/2] rounded-[2px] flex-shrink-0`}
    />
  )
}

export function LanguageSwitcher({ variant = 'default' }: LanguageSwitcherProps) {
  const { locale, setLocale } = useLocale()
  const t = useTranslations('common')

  // Minimal variant: Flag icon button
  if (variant === 'minimal') {
    return (
      <TooltipProvider>
        <Tooltip>
          <DropdownMenu>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/50"
                >
                  <Flag locale={locale} size="md" />
                  <span className="sr-only">{t('language')}</span>
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>{t('language')}</p>
            </TooltipContent>
            <DropdownMenuContent align="end" className="min-w-[140px]">
              {locales.map((loc) => {
                const isSelected = locale === loc
                return (
                  <DropdownMenuItem
                    key={loc}
                    onClick={() => setLocale(loc)}
                    className="cursor-pointer"
                  >
                    <Flag locale={loc} size="sm" />
                    <span className="flex-1">{localeNames[loc]}</span>
                    {isSelected && <Check className="h-4 w-4 text-primary" />}
                  </DropdownMenuItem>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </Tooltip>
      </TooltipProvider>
    )
  }

  // Default variant: Button with flag, label, and chevron
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1.5 px-2.5 text-muted-foreground hover:text-foreground hover:bg-muted/50"
        >
          <Flag locale={locale} size="sm" />
          <span className="text-sm font-medium">{localeCodes[locale]}</span>
          <ChevronRight className="h-3 w-3 rotate-90 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[140px]">
        {locales.map((loc) => {
          const isSelected = locale === loc
          return (
            <DropdownMenuItem
              key={loc}
              onClick={() => setLocale(loc)}
              className="cursor-pointer"
            >
              <Flag locale={loc} size="sm" />
              <span className="flex-1">{localeNames[loc]}</span>
              {isSelected && <Check className="h-4 w-4 text-primary" />}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
